// Pantalla para crear un impeachment (reporte) contra un usuario (versión web).
// Permite seleccionar el tipo de reporte y enviarlo.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useImpeachmentQuery, useImpeachmentMutation } from '../../hooks/useImpeachment';
import MegaButton from '../../components/UI/MegaButton';
import { impeachment } from '../../constants/impeachment';
import { FiArrowLeft } from 'react-icons/fi';

export default function UserImpeachmentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const targetUser = location.state || { id: null, full_name: 'Usuario' };

  // Valida que exista userId en location.state, si no redirige
  useEffect(() => {
    if (!targetUser?.id) {
      console.error('[UserImpeachmentScreen] No se recibió userId en location.state');
      navigate('/radar');
    }
  }, [targetUser?.id, navigate]);

  // Obtiene la sesión del usuario
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const user = session?.user;

  // Estados locales del componente
  const [impeachmentType, setImpeachmentType] = useState(null);
  const [isFormChanged, setIsFormChanged] = useState(false);

  // Hook para obtener impeachment existente
  // Se usa para verificar si ya existe un reporte previo
  const { isLoading: isLoadingImpeachment } = useImpeachmentQuery(user?.id, targetUser?.id);

  // Hook para mutación de impeachment
  const impeachmentMutation = useImpeachmentMutation();

  // Valida que exista sesión y usuario objetivo
  const isValidForm = user?.id && targetUser?.id && impeachmentType;

  // Maneja la selección de tipo de impeachment
  const handleSelectImpeachmentType = (typeIndex) => {
    setImpeachmentType(typeIndex + 1);
    setIsFormChanged(true);
  };

  // Muestra alerta de confirmación antes de enviar el reporte
  const showConfirmationAlert = () => {
    if (!isValidForm) return;

    if (window.confirm(`¿Informar problema con ${targetUser.full_name}?`)) {
      handleSubmitImpeachment();
    }
  };

  // Maneja el envío del impeachment usando TanStack Query
  const handleSubmitImpeachment = async () => {
    if (!isValidForm) return;

    try {
      setIsFormChanged(false);
      
      const impeachmentData = {
        gestor: user.id,
        demandado: targetUser.id,
        motivo: impeachmentType,
      };

      await impeachmentMutation.mutateAsync({
        impeachmentData,
        existingId: null,
      });

      alert('Reporte enviado, gracias.');

      // Navegación a pantalla de confirmación
      navigate('/report/confirmation', { state: targetUser });

    } catch (error) {
      alert('Error al enviar reporte. Inténtalo nuevamente.');
    }
  };

  // Loading mientras carga la sesión o información inicial
  if (!user || isLoadingImpeachment) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <p className="text-white">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900 flex flex-col">
      {/* Header */}
      <div className="h-20 px-4 flex items-center gap-4 border-b border-neutral-800 bg-neutral-900">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
        >
          <FiArrowLeft size={24} color="white" />
        </button>
        <h1 className="text-white text-lg">Informar de un problema</h1>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto p-4">
        <h2 className="text-white text-2xl text-center mb-6">
          ¿Qué problema quieres denunciar?
        </h2>

        {/* Lista de tipos de impeachment */}
        <div className="space-y-3">
          {impeachment.map((item, index) => (
            <button
              key={index}
              onClick={() => handleSelectImpeachmentType(index)}
              className={`w-full bg-neutral-800 p-4 rounded-lg text-left transition-colors ${
                impeachmentType === index + 1 ? 'border-2 border-primary' : 'border border-neutral-700'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-white text-lg font-semibold">
                  {item.title}
                </span>
                <div className={`w-4 h-4 rounded-full ${
                  impeachmentType === index + 1 ? 'bg-primary' : 'bg-neutral-600'
                }`} />
              </div>
              <p className="text-neutral-400 text-sm">
                {item.desc}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Botón de envío */}
      <div className="sticky bottom-0 bg-neutral-900 border-t border-neutral-700 h-16 flex items-center justify-end px-4">
        <MegaButton
          labelText="Siguiente"
          onPress={showConfirmationAlert}
          loading={impeachmentMutation.isPending}
          disabled={!isFormChanged || impeachmentMutation.isPending}
          className="!h-12 !py-0"
        />
      </div>
    </div>
  );
}
